import type * as React from 'react';
import { useRecoilState } from 'recoil';

import * as api from '@gitbook/api-client';
import { Heading, Panel, PanelBody, Tab, Tabs } from '@gitbook/spine-react';

import { capitalizeFirstLetter } from '../../../utils';
import { sitePreviewAtom } from '../..';
import type { SitesCustomizationPanelProps } from '../types';
import { setThemedColor } from '../utils';
import { ColorPickerForm } from './ColorPickerForm';
import { SemanticColorSettings } from './SemanticColorsSettings';
import { ThemeThumbnails } from './ThemeThumbnails';
import { TintSettings, updateTintColor } from './TintSettings';

export function ThemesPanel(props: SitesCustomizationPanelProps): React.ReactElement {
    const { settings, setSettings } = props;
    const [previewOptions, setPreviewOptions] = useRecoilState(sitePreviewAtom);

    return (
        <Panel>
            <Heading>Themes</Heading>
            <PanelBody>
                <ThemeThumbnails {...props} previewThemeMode={previewOptions.theme} />

                <div className="flex flex-col gap-4">
                    <Tabs className="border-b">
                        {Object.values(api.CustomizationThemeMode).map((mode) => (
                            <Tab
                                key={mode}
                                active={mode === previewOptions.theme}
                                onPress={() =>
                                    setPreviewOptions((prev) => ({ ...prev, theme: mode }))
                                }
                                label={`${capitalizeFirstLetter(mode)} mode`}
                            />
                        ))}
                    </Tabs>

                    <ColorPickerForm
                        {...props}
                        label="Primary Color"
                        description="Used for main elements such as buttons and links"
                        currentColor={settings.styling.primaryColor[previewOptions.theme]}
                        setColor={(newColor) => {
                            setSettings((prev) => ({
                                ...prev,
                                styling: {
                                    ...prev.styling,
                                    primaryColor: setThemedColor(
                                        prev.styling?.primaryColor,
                                        previewOptions.theme,
                                        newColor
                                    ),
                                    ...(settings.styling.tint
                                        ? {
                                              tint: updateTintColor(prev, previewOptions.theme, {
                                                  type: 'primaryColor',
                                                  newColor,
                                              }),
                                          }
                                        : {}),
                                },
                            }));
                        }}
                    />

                    <TintSettings {...props} previewThemeMode={previewOptions.theme} />
                    <SemanticColorSettings {...props} previewThemeMode={previewOptions.theme} />
                </div>
            </PanelBody>
        </Panel>
    );
}
